import type { StateCreator } from "zustand";
import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import type { SprintMissionPoolSchema } from "@/types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

export interface SprintMissionSlice {
  /**
   * Assembled view objects — a THREE-way join (campaigns ⋈ mission_progress ⋈
   * sprint_mission_pool), rebuilt on any of the three changing. Deeper than the prize drop's
   * two-way join because a Sprint Mission still has per-player task progress; the pool stream
   * only adds the shared counter on top.
   */
  sprintMissions: SprintMission[];
  setSprintMissions: (sprintMissions: SprintMission[]) => void;
  clearSprintMissions: () => void;

  /** Raw `sprint_mission_pool` stream, keyed by campaign key (the volatile tail). */
  sprintMissionPoolById: Record<CampaignKey, SprintMissionPoolSchema>;
  sprintMissionPoolStream: CampaignStreamState;
  setSprintMissionPool: (pools: SprintMissionPoolSchema[]) => void;
  setSprintMissionPoolSubscribed: (subscribed: boolean) => void;
  setSprintMissionPoolLoading: (loading: boolean) => void;
  clearSprintMissionPool: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createSprintMissionSlice: StateCreator<SprintMissionSlice> = (
  set
) => ({
  sprintMissions: [],
  setSprintMissions: (sprintMissions) => set({ sprintMissions }),
  clearSprintMissions: () => set({ sprintMissions: [] }),

  sprintMissionPoolById: {},
  sprintMissionPoolStream: initialStream,
  setSprintMissionPool: (pools) =>
    set(() => {
      const byId: Record<CampaignKey, SprintMissionPoolSchema> = {};
      for (const p of pools) byId[campaignKey(p)] = p;
      return { sprintMissionPoolById: byId };
    }),
  setSprintMissionPoolSubscribed: (subscribed) =>
    set((state) => ({
      sprintMissionPoolStream: {
        ...state.sprintMissionPoolStream,
        subscribed,
        loading: false,
      },
    })),
  setSprintMissionPoolLoading: (loading) =>
    set((state) => ({
      sprintMissionPoolStream: { ...state.sprintMissionPoolStream, loading },
    })),
  clearSprintMissionPool: () =>
    set({
      sprintMissionPoolById: {},
      sprintMissionPoolStream: initialStream,
    }),
});

/** Resolve a Sprint Mission campaign by its custom_id or numeric id (as string). */
export function selectSprintMissionById(
  state: AppStore,
  id: string
): SprintMission | undefined {
  return state.sprintMissions.find((lm) => matchesKey(lm.campaign, id));
}
